import React from "react";
import { Route } from "react-router-dom";
import { useSelector } from 'react-redux';

import Payments from './pages/payments';
import PaymentLinks from './pages/payment-links';
import Ecommerce from './pages/ecommerce';
import EcommerceAssign from './pages/ecommerce/assign';
import Refund from './pages/refund';
import Refunds from './pages/refunds';
import Chargebacks from "./pages/chargebacks";
import Payment from './pages/payment';
import CreatePaymentLink from './pages/payment-link/create';
import PaymentLink from './pages/payment-link/link';
import PaymentLinkAssign from './pages/payment-link/assign';
import PaymentLinkReAssign from './pages/payment-link/re-assign';
import BankDeposits from "./pages/bank-deposits";
import ScheduledPayments from './pages/scheduled-payments';

import RedirectRenderer from "src/components/redirect-renderer";
import HasFeature from "src/utils/has-feature";
import HasRole from "src/utils/has-role";
import GroupMenu from "src/components/group-menu";
import GroupContainer from "src/components/group-container";

import { ArrowUturnUpIcon, BanknotesIcon, CalendarIcon, LinkIcon, ReceiptRefundIcon, WindowIcon } from "@heroicons/react/24/outline";

export const getRoutes = (account, features) => [
  { name: 'Bank Deposits', description: 'Monitor and tag deposits made into your bank account', path: '/payments/bank-deposits', check: HasRole(account, ['open-banking:view']) && HasFeature(features, ['aisp']) },
  { name: 'Chargebacks', description: 'Monitor and manage chargebacks and customer disputes', path: '/payments/chargebacks', check: HasRole(account, ['chargebacks:view']) && HasFeature(features, ['chargebacks']) },
  { name: 'Ecommerce Sessions', description: 'Monitor and manage ecommerce sessions', path: '/payments/ecommerce', check: HasFeature(features, ['ecommerce']) && HasRole(account, ['booking-and-payment:organisation-access']) },
  { name: 'Payments', description: 'Monitor and manage your customer payments', path: '/payments', check: true },
  { name: 'Payment Links', description: 'Monitor and manage your payment links', path: '/payments/payment-links', check: true },
  { name: 'Refunds', description: 'Monitor and manage refunds on transactions', path: '/payments/refunds', check: HasRole(account, ['refund:approve']) && HasFeature(features, ['refunds']) },
  { name: 'Scheduled Payments', description: 'Manage scheduled payments across all bookings', path: '/payments/scheduled', check: true },
  { name: 'Create Payment Link', description: 'Generate a secure, share-ready payment link', path: '/payment-link/create', check: HasRole(account, ['booking-and-payment:create:payment']) },
];

const Layout = ({ children }) => {
  const { organisation: { features }, account: { account } } = useSelector((state) => state);

  const items = [
    { name: 'Bank Deposits', description: 'Monitor and tag deposits made into your bank account', icon: BanknotesIcon, path: '/payments/bank-deposits', check: HasRole(account, ['open-banking:view']) && HasFeature(features, ['aisp']) },
    { name: 'Chargebacks', description: 'Monitor and manage chargebacks and customer disputes', icon: ArrowUturnUpIcon, path: '/payments/chargebacks', check:HasRole(account, ['chargebacks:view']) && HasFeature(features, ['chargebacks'])  },
    { name: 'Ecommerce Sessions', description: 'Monitor and manage ecommerce sessions, generated using our SDK', icon: WindowIcon, path: '/payments/ecommerce', check:HasFeature(features, ['ecommerce']) && HasRole(account, ['booking-and-payment:organisation-access']) },
    { name: 'Payments', description: 'Monitor and manage your customer payments', icon: BanknotesIcon, path: '/payments', check: true },
    { name: 'Payment Links', description: 'Monitor and manage your payment links', icon: LinkIcon, path: '/payments/payment-links', check: true },
    { name: 'Refunds', description: 'Monitor and manage refunds on transactions', icon: ReceiptRefundIcon, path: '/payments/refunds', check:HasRole(account, ['refund:approve']) && HasFeature(features, ['refunds']) },
    { name: 'Scheduled Payments', description: 'Manage scheduled payments across all bookings', icon: CalendarIcon, path: '/payments/scheduled', check: true },

    { name: 'Manage Payment Link', parent: 'Payment Links', description: 'View, edit, and share a payment link so customers can pay securely', visible: false, icon: LinkIcon, path: '/payment-links/:id', check: true },
    { name: 'Assign Payment Link', parent: 'Payment Links', description: 'Assign / match payment link to a booking', visible: false, icon: LinkIcon, path: '/payment-links/:id/assign', check: true },
    { name: 'Re-assign Payment Link', parent: 'Payment Links', description: 'Re-assign payment link to another booking', visible: false, icon: LinkIcon, path: '/payment-links/:id/re-assign', check: true },
    { name: 'Create Payment Link', parent: 'Payment Links', description: 'Generate a secure, share-ready link so customers can pay', visible: false, icon: LinkIcon, path: '/payment-link/create', check: HasRole(account, ['booking-and-payment:create:payment']) },

    { name: 'Payment', parent: 'Payments', description: 'View, edit, and manage a payment', visible: false, icon: BanknotesIcon, path: '/payment/:id', check: true },
    { name: 'Payment', parent: 'Payments', description: 'View, edit, and manage a payment', visible: false, icon: BanknotesIcon, path: '/payment/:id/:route', check: true },
    { name: 'Disbursal Detail', parent: 'Payments', description: 'An overview and breakdown of a disbursal to your account', visible: false, icon: BanknotesIcon, path: '/payments/batch', check: true },
  ];

  return (
    <div id="content">
      <GroupMenu items={items} title="Payments" />
      <GroupContainer>
        {children}
      </GroupContainer>
    </div>
  )
};

const redirects = [
  { from: '/batch', to: '/payments/batch' },
  { from: '/ecommerce', to: '/payments/ecommerce' },
  { from: '/transactions', to: '/payments' },
  { from: '/payment-links', to: '/payments/payment-links' },
  { from: '/refunds', to: '/payments/refunds' },
  { from: '/refunds/:id', to: '/payments/refund/:id' },
  { from: '/chargebacks', to: '/payments/chargebacks' },
  { from: '/transaction/:id', to: '/payment/:id' },
  { from: '/transaction/:id/payout', to: '/payment/:id/payout' },
  { from: '/transaction/:id/settlement', to: '/payment/:id/settlement' },
  { from: '/transaction/:id/bank', to: '/payment/:id/bank' },
];

const PaymentsRouter = () => {
  const { organisation: { features }, account: { account } } = useSelector((state) => state);

  if (account?.id) {
    return (
      <React.Fragment>

        { RedirectRenderer({ redirects }) }

        <Route path="/payments" exact component={() => <Layout><Payments /></Layout>} />
        <Route path="/payments/payment-links" component={() => <Layout><PaymentLinks /></Layout>} />

        { /* ECOMMERCE **/}
        {HasFeature(features, ['ecommerce']) && HasRole(account, ['booking-and-payment:organisation-access']) && (
          <React.Fragment>
            <Route exact path={`/payments/ecommerce`} component={() => <Layout><Ecommerce /></Layout>} />
            <Route exact path={`/ecommerce/:id/assign`} component={() => <Layout><EcommerceAssign /></Layout>} />
          </React.Fragment>
        )}

        { /* BANK DEPOSITS **/}
        {HasRole(account, ['open-banking:view']) && HasFeature(features, ['aisp']) && (
          <Route exact path={`/payments/bank-deposits`} component={() => <Layout><BankDeposits /></Layout>} />
        )}

        { /* BATCHES **/}
        {HasRole(account, ['payouts:view']) && (
          <Route exact path={`/payments/batch`} component={() => <Layout><Payments /></Layout>} />
        )}

        { /* CHARGEBACKS **/}
        { HasRole(account, ['chargebacks:view']) && HasFeature(features,['chargebacks']) && (
          <Route exact path={`/payments/chargebacks`} component={() => <Layout><Chargebacks /></Layout>} />
        )}

        { /* REFUNDS **/}
        {HasRole(account, ['refund:approve']) && HasFeature(features, ['refunds']) && (
          <React.Fragment>
            <Route exact path={`/payments/refunds`} component={() => <Layout><Refunds /></Layout>} />
            <Route exact path={`/payments/refund/:id`} component={() => <Layout><Refund /></Layout>} />
          </React.Fragment>
        )}
        
        { /* PAYMENT **/}
        { HasFeature(features, ['booking-and-payment']) && (
          <React.Fragment>
            <Route exact path={`/payment/:id`} component={() => <Layout><Payment /></Layout>}/>
            <Route exact path={`/payment/:id/payout`} component={() => <Layout><Payment /></Layout>}/>
            <Route exact path={`/payment/:id/settlement`} component={() => <Layout><Payment /></Layout>}/>
            <Route exact path={`/payment/:id/bank`} component={() => <Layout><Payment /></Layout>}/>
            { HasRole(account, ['booking-and-payment:create:payment']) && (
              <Route exact path={`/payment-link/create`} component={() => <Layout><CreatePaymentLink /></Layout>}/>
            )}

            <Route exact path={`/payment-links/:id`} component={() => <Layout><PaymentLink /></Layout>}/>
            <Route exact path={`/payment-links/:id/assign`} component={() => <Layout><PaymentLinkAssign /></Layout>}/>
            <Route exact path={`/payment-links/:id/re-assign`} component={() => <Layout><PaymentLinkReAssign /></Layout>}/>
          </React.Fragment>
        )}

        { /* SCHEDULED PAYMENTS **/}
        { HasFeature(features, ['booking-and-payment']) && (
          <Route exact path={`/payments/scheduled`} component={() => <Layout><ScheduledPayments /></Layout>}/>
        )}

      </React.Fragment>
    );
  }

  return <React.Fragment />;
};

export default PaymentsRouter;
